// Redesign tokens for makeStyles / inline styles. Values resolve against
// common/styles/tokens.css, which holds the hexes — never duplicate one here.
// Names mirror mentorship-droid/src/constants/colors.js so both apps read alike.

const v = name => `var(--${name})`;

export const color = {
  SURFACE: v('ui-surface'),
  SURFACE_MUTED: v('ui-surface-muted'),
  PAPER: v('ui-paper'),
  TEXT: v('ui-text'),
  TEXT_MUTED: v('ui-text-muted'),
  TEXT_SUBTLE: v('ui-text-subtle'),
  DIVIDER: v('ui-divider'),
  BORDER: v('ui-border'),
  ACCENT: v('ui-accent'),
  ON_ACCENT: v('ui-on-accent'),
  ACCENT_TEXT: v('ui-accent-text'),
  ACCENT_TINT: v('ui-accent-tint'),
  ACCENT_SOFT: v('ui-accent-soft'),
  FIELD_BG: v('ui-field-bg'),
  DANGER: v('ui-danger'),
  ON_DANGER: v('ui-on-danger'),
  DANGER_TINT: v('ui-danger-tint'),
  SUCCESS: v('ui-success'),
  SUCCESS_TINT: v('ui-success-tint'),
  PURPLE: v('ui-purple'),
  ON_PURPLE: v('ui-on-purple'),
  PURPLE_TINT: v('ui-purple-tint'),
  TERTIARY: v('ui-tertiary'),
  ON_TERTIARY: v('ui-on-tertiary'),
  TERTIARY_TINT: v('ui-tertiary-tint'),
  INFO: v('ui-info'),
  EVENT_SURFACE: v('ui-event-surface'),
  MUTED_CTA: v('ui-muted-cta'),
  ART_SKIN: v('ui-art-skin'),
  ART_HAIR: v('ui-art-hair'),
  ART_MENTEE: v('ui-art-mentee'),
  ART_MENTOR: v('ui-art-mentor'),
  ART_SHADE: v('ui-art-shade'),
  PARTNER: v('ui-partner'),
  PARTNER_TINT: v('ui-partner-tint'),

  CARD_BORDER: v('ui-card-border'),
  CARD_TITLE: v('ui-card-title'),
  CARD_BODY: v('ui-card-body'),
  RING_FILL: v('ui-ring-fill'),
  RING_TRACK: v('ui-ring-track'),
  CHIP_BG: v('ui-chip-bg'),
  CHIP_BORDER: v('ui-chip-border'),
  CHIP_TEXT: v('ui-chip-text'),
  CHIP_OVERDUE_TEXT: v('ui-chip-overdue-text'),
  AVATAR_BG: v('ui-avatar-bg'),
  AVATAR_TEXT: v('ui-avatar-text'),
  AVATAR_DONE_BG: v('ui-avatar-done-bg'),
  AVATAR_DONE_TEXT: v('ui-avatar-done-text'),
  AVATAR_ROSTER_BG: v('ui-avatar-roster-bg'),
  AVATAR_RING: v('ui-avatar-ring'),
  SECTION_TITLE: v('ui-section-title'),
  SECTION_ICON_BG: v('ui-section-icon-bg'),
  SECTION_ICON_BORDER: v('ui-section-icon-border'),
  ROLE_CHIP_BG: v('ui-role-chip-bg'),
  ROLE_CHIP_TEXT: v('ui-role-chip-text'),

  SCRIM: v('mt-scrim'),
};

// Matching flow. Names mirror mentorship-droid's MATCH_* so both apps read alike.
export const match = {
  CARD_BORDER: v('ui-match-card-border'),
  CARD_SELECTED_BG: v('ui-match-card-selected-bg'),
  CARD_SELECTED_BORDER: v('ui-match-card-selected-border'),
  CARD_MUTED_BG: v('ui-match-card-muted-bg'),
  ACCENT: v('ui-match-accent'),
  ACCEPT_BG: v('ui-match-accept-bg'),
  ACCEPT_FG: v('ui-match-accept-fg'),
  REJECT_BG: v('ui-match-reject-bg'),
  BADGE_DANGER_BG: v('ui-match-badge-danger-bg'),
  BADGE_DANGER_FG: v('ui-match-badge-danger-fg'),
  BADGE_SUCCESS_BG: v('ui-match-badge-success-bg'),
  BADGE_SUCCESS_FG: v('ui-match-badge-success-fg'),
  BADGE_NEUTRAL_BG: v('ui-match-badge-neutral-bg'),
  BADGE_NEUTRAL_FG: v('ui-match-badge-neutral-fg'),
  CHIP_BG: v('ui-match-chip-bg'),
  CHIP_FG: v('ui-match-chip-fg'),
  CHIP_MUTED_BG: v('ui-match-chip-muted-bg'),
  CHIP_MUTED_FG: v('ui-match-chip-muted-fg'),
  CHIP_CLASH_BG: v('ui-match-chip-clash-bg'),
  CHIP_CLASH_FG: v('ui-match-chip-clash-fg'),
  EXPIRY_OK: v('ui-match-expiry-ok'),
  EXPIRY_SOON: v('ui-match-expiry-soon'),
  EXPIRY_URGENT: v('ui-match-expiry-urgent'),
  AVATAR_BG: v('ui-match-avatar-bg'),
  AVATAR_FG: v('ui-match-avatar-fg'),
  SLOT_BG: v('ui-match-slot-bg'),
  STATUS_OPEN: v('ui-match-status-open'),
};

export const font = {
  SANS: v('ui-font-sans'),
  DISPLAY: v('ui-font-display'),
  REGULAR: v('ui-weight-regular'),
  MEDIUM: v('ui-weight-medium'),
  SEMIBOLD: v('ui-weight-semibold'),
  BOLD: v('ui-weight-bold'),
  EXTRABOLD: v('ui-weight-extrabold'),
};

// Ready-made type ramps — spread into a makeStyles rule rather than
// re-declaring size/leading/tracking at each call site.
export const type = {
  pageTitle: {
    fontFamily: font.SANS,
    fontSize: v('ui-text-page-title'),
    lineHeight: v('ui-leading-page-title'),
    letterSpacing: v('ui-tracking-page-title'),
    fontWeight: font.BOLD,
  },
  panelTitle: {
    fontFamily: font.SANS,
    fontSize: v('ui-text-panel-title'),
    lineHeight: v('ui-leading-panel-title'),
    letterSpacing: v('ui-tracking-panel-title'),
    fontWeight: font.BOLD,
  },
  sectionTitle: {
    fontFamily: font.DISPLAY,
    fontSize: v('ui-text-panel-title'),
    fontWeight: font.BOLD,
    color: color.SECTION_TITLE,
  },
  cardHeading: {
    fontFamily: font.SANS,
    fontSize: v('ui-text-card-heading'),
    lineHeight: v('ui-leading-card-heading'),
    letterSpacing: v('ui-tracking-card-heading'),
    fontWeight: font.BOLD,
  },
  sessionTitle: {
    fontFamily: font.SANS,
    fontSize: v('ui-text-session-title'),
    lineHeight: v('ui-leading-session-title'),
    fontWeight: font.BOLD,
  },
  body: {
    fontFamily: font.SANS,
    fontSize: v('ui-text-body'),
    lineHeight: v('ui-leading-body'),
    fontWeight: font.REGULAR,
  },
  meta: {
    fontFamily: font.SANS,
    fontSize: v('ui-text-meta'),
    lineHeight: v('ui-leading-meta'),
    fontWeight: font.SEMIBOLD,
  },
  // 11px SemiBold without the eyebrow's caps/tracking — badges, avatar initials.
  label: {
    fontFamily: font.SANS,
    fontSize: v('ui-text-eyebrow'),
    lineHeight: v('ui-leading-eyebrow'),
    fontWeight: font.SEMIBOLD,
  },
  eyebrow: {
    fontFamily: font.SANS,
    fontSize: v('ui-text-eyebrow'),
    lineHeight: v('ui-leading-eyebrow'),
    letterSpacing: v('ui-tracking-eyebrow'),
    fontWeight: font.SEMIBOLD,
    textTransform: 'uppercase',
  },
  chip: {
    fontFamily: font.SANS,
    fontSize: v('ui-text-chip'),
    fontWeight: font.SEMIBOLD,
  },
  chipSmall: {
    fontFamily: font.SANS,
    fontSize: v('ui-text-chip-sm'),
    letterSpacing: v('ui-tracking-chip-sm'),
    fontWeight: font.BOLD,
    textTransform: 'uppercase',
  },
  accentDate: {
    fontFamily: font.SANS,
    fontSize: v('ui-text-accent-date'),
    fontWeight: font.EXTRABOLD,
    color: color.PURPLE,
  },
};

export const radius = {
  CARD: v('ui-radius-card'),
  MEDIA_CARD: v('ui-radius-media-card'),
  PANEL: v('ui-radius-panel'),
  CTA: v('ui-radius-cta'),
  CONTROL: v('ui-radius-control'),
  PILL: v('ui-radius-pill'),
};

export const space = {
  CONTAINER_MAX: v('ui-container-max'),
  CARD_PADDING: v('ui-card-padding'),
  SECTION_GAP: v('ui-section-gap'),
  GRID_GAP_X: v('ui-grid-gap-x'),
  GRID_GAP_Y: v('ui-grid-gap-y'),
};

export const state = {
  DISABLED_OPACITY: v('ui-disabled-opacity'),
};

export const shadow = {
  CARD: v('ui-shadow-card'),
  PANEL: v('ui-shadow-panel'),
  CTA: v('ui-shadow-cta'),
  POPOVER: v('ui-shadow-popover'),
};

const tokens = {color, match, font, type, radius, space, state, shadow};

export default tokens;
